import type React from 'react'
import styled, { css } from 'styled-components'

import ContentWithLoadingOverlay from '@components/ui/ContentWithLoadingOverlay'
import { Typography } from '@components/ui/Typography'
import type { PaperProps } from '@components/ui/Wrappers'
import { Paper } from '@components/ui/Wrappers'

type CardVariant = 'default' | 'details'

const CardHeaderTypography = styled(Typography)`
  padding: var(--card-header-padding);
  border-bottom: var(--card-header-border);
  display: flex;
  align-items: center;
  gap: 8px;
  height: var(--card-header-height, auto);
  background-color: var(--card-header-background, transparent);
`

const StyledContentWithLoadingOverlay = styled(ContentWithLoadingOverlay)``

export const StyledCard = styled(Paper)<{ $withoutPadding: boolean; $variant?: CardVariant }>`
  --card-padding: 15px 20px;
  --card-header-padding: var(--card-padding);
  --card-header-border: 1px solid ${({ theme }) => theme.colors.common.paperStroke};

  ${({ $variant, theme }) =>
    $variant === 'details' &&
    css`
      --card-padding: 35px 30px 45px;
      --card-header-padding: 0 30px;
      --card-header-height: 64px;
      --card-header-background: ${theme.colors.gray[102]};
      --card-header-border: 1px solid ${theme.colors.gray[105]};

      /* the header band is edge-to-edge, so it has to be clipped to the card's border radius */
      overflow: hidden;
    `}

  & > ${StyledContentWithLoadingOverlay} {
    padding: ${({ $withoutPadding }) => ($withoutPadding ? '0' : 'var(--card-padding)')};
  }
`

interface CardProps {
  children: React.ReactNode
  headingTag?: 'h2' | 'h3'
  icon?: React.ReactNode
  loading?: boolean
  tag?: PaperProps['tag']
  title: NonNullable<React.ReactNode> | NonNullable<React.ReactNode>[]
  variant?: CardVariant
  withoutPadding?: boolean
}

export function Card({
  children,
  headingTag = 'h2',
  icon,
  loading = false,
  tag = 'article',
  title,
  variant = 'default',
  withoutPadding = false,
}: CardProps) {
  return (
    <StyledCard tag={tag} $withoutPadding={withoutPadding} $variant={variant}>
      <CardHeaderTypography tag={headingTag} size="heading-5" color={variant === 'details' ? 'gray-70' : 'gray-40'}>
        {icon}
        {title}
      </CardHeaderTypography>
      <StyledContentWithLoadingOverlay loading={loading}>{children}</StyledContentWithLoadingOverlay>
    </StyledCard>
  )
}
